import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Star, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Button from "./ui/Button";
import { formatDay } from "../lib/utils";

// Visits the customer chose "Not now" for, so we ask once and don't nag.
// They can still review them from their appointments.
const DISMISSED_KEY = "shuwa_review_prompts_dismissed";

const dismissedIds = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY)) || [];
  } catch {
    return [];
  }
};

// Not while the customer is in the middle of booking, or signed in to
// run a business or the admin area.
const QUIET_PATHS = /^\/(business|admin|login|register|forgot-password)(\/|$)|\/book$/;

/**
 * After every completed visit, asks the customer how it went — as soon as
 * they next open SHUWA — until they review it or say "Not now".
 */
export default function ReviewPrompt() {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const [appointment, setAppointment] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [thanks, setThanks] = useState(false);

  const userId = user?.id;
  // The "Leave a review" link in the email and notification names the visit.
  const requested = new URLSearchParams(search).get("review");

  useEffect(() => {
    if (!userId) {
      setAppointment(null);
      return;
    }

    api
      .get("/reviews/pending")
      .then(({ data }) => {
        const dismissed = dismissedIds();
        const pending = data || [];
        setAppointment(pending.find((a) => a.id === requested) || pending.find((a) => !dismissed.includes(a.id)) || null);
      })
      .catch(() => {});
  }, [userId, requested]);

  const close = () => {
    if (appointment && !thanks) {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissedIds(), appointment.id].slice(-50)));
    }
    setAppointment(null);
  };

  useEffect(() => {
    if (!appointment) return;
    const onKey = (e) => e.key === "Escape" && !busy && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!appointment || QUIET_PATHS.test(pathname)) return null;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/reviews", { appointment_id: appointment.id, rating, comment: comment.trim() || undefined });
      setThanks(true);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not save your review.");
    } finally {
      setBusy(false);
    }
  };

  const business = appointment.business?.name;
  const services = appointment.services?.map((s) => s.name_snapshot).filter(Boolean).join(", ");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div role="dialog" aria-modal="true" aria-label="Review your visit" className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        {thanks ? (
          <div className="py-4 text-center">
            <h2 className="font-montserrat text-lg font-bold text-slate-900">Thanks for your review!</h2>
            <p className="mt-1 text-sm text-muted-foreground">It helps other customers choose, and helps {business} grow.</p>
            <Button className="mt-5" onClick={close}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-montserrat text-lg font-bold text-slate-900">How was your visit to {business}?</h2>
                <p className="text-sm text-muted-foreground">
                  {[services, formatDay(appointment.appointment_date)].filter(Boolean).join(" · ")}
                </p>
              </div>
              <button onClick={close} disabled={busy} aria-label="Close" className="text-muted-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex justify-center gap-1.5 py-2" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} star${n > 1 ? "s" : ""}`} onClick={() => setRating(n)}>
                  <Star className={`h-9 w-9 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Tell others about it (optional)"
              className="mt-2 w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-primary"
            />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="ghost" disabled={busy} onClick={close}>
                Not now
              </Button>
              <Button disabled={!rating || busy} onClick={submit}>
                {busy ? "Sending…" : "Send review"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
